import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { scheduleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { CalendarDays, Search, Filter, Clock, CheckCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const EVENT_STATUS_OPTIONS = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'tentative', label: 'Tentative' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const SCHEDULE_TYPE_OPTIONS = [
  'Trucking', 'Crew Call', 'Setup', 'Strike', 'Show', 'Rehearsal/Tech Check', 'Meal/Break', 'Travel'
];

const getEventStatusColor = (status) => {
  switch (status) {
    case 'inquiry': return 'border-red-500/30 bg-red-500/10 text-red-400';
    case 'tentative': return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400';
    case 'confirmed': return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400';
    case 'completed': return 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400';
    case 'cancelled': return 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400';
    default: return 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400';
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'M/d/yyyy');
  } catch {
    return dateStr;
  }
};

const formatTime = (timeStr) => {
  if (!timeStr) return '-';
  return timeStr;
};

const GlobalSchedule = () => {
  const [scheduleItems, setScheduleItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    event_search: '',
    schedule_type: '',
    event_status: '',
    start_date: '',
    end_date: '',
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    loadSchedule();
  }, [page, limit, sortBy, sortOrder]);

  const loadSchedule = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (filters.event_search) params.event_search = filters.event_search;
      if (filters.schedule_type) params.schedule_type = filters.schedule_type;
      if (filters.event_status) params.event_status = filters.event_status;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      
      const response = await scheduleAPI.getGlobal(params);
      
      // Handle paginated response format
      if (response.data.data && response.data.pagination) {
        setScheduleItems(response.data.data);
        setPagination(response.data.pagination);
      } else {
        // Fallback for old response format
        setScheduleItems(response.data);
        setPagination({
          total: response.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1); // Reset to first page on search
    loadSchedule();
  };

  const clearFilters = () => {
    setFilters({
      event_search: '',
      schedule_type: '',
      event_status: '',
      start_date: '',
      end_date: '',
    });
    setPage(1);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const renderPagination = () => {
    const { total, total_pages, has_next, has_prev } = pagination;
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, page - Math.floor(maxVisible / 2));
      let end = Math.min(total_pages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border/50">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{total > 0 ? startItem : 0}</span> to{' '}
          <span className="font-medium text-foreground">{endItem}</span> of{' '}
          <span className="font-medium text-foreground">{total}</span> schedule items
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows:</span>
            <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
              <SelectTrigger className="w-16 h-8 bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(1)}
              disabled={!has_prev}
              data-testid="first-page"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(page - 1)}
              disabled={!has_prev}
              data-testid="prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage(pageNum)}
                  data-testid={`page-${pageNum}`}
                >
                  {pageNum}
                </Button>
              ))}
            </div>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(page + 1)}
              disabled={!has_next}
              data-testid="next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setPage(total_pages)}
              disabled={!has_next}
              data-testid="last-page"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="global-schedule">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
          <CalendarDays className="h-8 w-8 text-cyan-400" />
          Global Schedule
        </h1>
        <p className="text-muted-foreground mt-1">
          View all schedule items across all events
        </p>
      </div>

      {/* Filters */}
      <Card className="border-border/50 bg-card/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Event Name / Flex #</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={filters.event_search}
                  onChange={(e) => setFilters({ ...filters, event_search: e.target.value })}
                  placeholder="Search events..."
                  className="bg-background/50 pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Type</label>
              <Select
                value={filters.schedule_type || 'all'}
                onValueChange={(value) => setFilters({ ...filters, schedule_type: value === 'all' ? '' : value })}
              >
                <SelectTrigger className="bg-background/50">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {SCHEDULE_TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Event Status</label>
              <Select
                value={filters.event_status || 'all'}
                onValueChange={(value) => setFilters({ ...filters, event_status: value === 'all' ? '' : value })}
              >
                <SelectTrigger className="bg-background/50">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {EVENT_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Start Date</label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="bg-background/50"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">End Date</label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="bg-background/50"
              />
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button onClick={handleSearch} data-testid="search-btn">
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Table */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
            </div>
          ) : scheduleItems.length > 0 ? (
            <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center">
                        Date
                        {getSortIcon('date')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('time')}
                    >
                      <div className="flex items-center">
                        Time
                        {getSortIcon('time')}
                      </div>
                    </TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('event_name')}
                    >
                      <div className="flex items-center">
                        Event
                        {getSortIcon('event_name')}
                      </div>
                    </TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Event Status</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduleItems.map((item) => (
                    <TableRow key={item.id} className="border-border/50 hover:bg-muted/30">
                      <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                      <TableCell className="font-mono">{formatTime(item.time)}</TableCell>
                      <TableCell>{item.duration ? `${item.duration} min` : '-'}</TableCell>
                      <TableCell>
                        <Link 
                          to={`/events/${item.event_id}`}
                          className="hover:text-primary transition-colors"
                        >
                          {item.event_name}
                          {item.flex_number && (
                            <span className="text-muted-foreground text-xs ml-1">#{item.flex_number}</span>
                          )}
                        </Link>
                      </TableCell>
                      <TableCell>{item.action || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{item.location || '-'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {item.schedule_types?.map((type) => (
                            <Badge key={type} variant="outline" className="text-xs">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getEventStatusColor(item.event_status)}>
                          {item.event_status || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.status === 'Confirmed' ? (
                          <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Confirmed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-yellow-500/30 bg-yellow-500/10 text-yellow-400">
                            <Clock className="h-3 w-3 mr-1" />
                            Tentative
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-w-[200px] truncate">
                        {item.notes || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {renderPagination()}
            </>
          ) : (
            <div className="text-center py-12">
              <CalendarDays className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No schedule items found</p>
              <p className="text-sm text-muted-foreground/60 mt-1">
                Try adjusting your filters or add schedule items to events
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GlobalSchedule;
